import {createSlice, PayloadAction, type Dispatch, type UnknownAction} from '@reduxjs/toolkit';
import {RootState} from "../../store";
import {CallStateEnum} from "../../../components/Dialer/callState";
import {Msisdn} from "../Msisdns/msisdnService.ts";

interface DialerState {
    isDialerHidden: boolean;
    autoHideDialer: boolean;
    isInCalls: boolean;
    selectedPhoneNumber: any | undefined;
    selectedMicrophone: string | null;
    selectedSpeaker: string ;
    autoGainControl: boolean;
    incomingCall: boolean;
    callFrom: any;
    callTo: Msisdn | undefined;
    callState: CallStateEnum;
    phoneNumber: string;
    selectedPrefix: {
        isoCountry: string;
        prefix: string | null;
    };
    showKeypad: boolean;
    sipInstanceId: string | null;
    micRequired: boolean;
    isOffline: boolean;
    navigateTo: any;
    ivrKeyName: string;
    isRingbackPlaying: boolean;
    isConnectingSoundPlaying: boolean;
    isSpam: string;
    isRingingPlaying: boolean;
    inCallSince?: number;
    callOwner?: CallEngineTag;
    secondaryCall?: EngineCall;
}

export type CallEngineTag = 'sip' | 'matrix';

export interface EngineCallFields {
    callState: CallStateEnum;
    incomingCall: boolean;
    callFrom: unknown;
    callTo: Msisdn | undefined | null;
    isSpam: string;
    ivrKeyName: string;
    inCallSince?: number;
}

export interface EngineCall extends EngineCallFields {
    engine: CallEngineTag;
}

const idleEngineCall = (engine: CallEngineTag): EngineCall => ({
    engine,
    callState: CallStateEnum.IDLE,
    incomingCall: false,
    callFrom: null,
    callTo: undefined,
    isSpam: '',
    ivrKeyName: '',
    inCallSince: undefined,
});

const engineOf = (action: {type: string; meta?: unknown}): CallEngineTag | undefined =>
    (action.meta as {engine?: CallEngineTag} | undefined)?.engine;

const isSettled = (fields: EngineCallFields): boolean => fields.callState === CallStateEnum.IDLE && !fields.incomingCall;

const promoteSecondary = (state: DialerState): void => {
    const next = state.secondaryCall;
    state.secondaryCall = undefined;
    state.callOwner = next?.engine;
    if (!next) {
        return;
    }
    state.callState = next.callState;
    state.incomingCall = next.incomingCall;
    state.callFrom = next.callFrom;
    state.callTo = next.callTo ?? undefined;
    state.isSpam = next.isSpam;
    state.ivrKeyName = next.ivrKeyName;
    state.inCallSince = next.inCallSince;
};

const writeEngineCall = (
    state: DialerState,
    action: {type: string; meta?: unknown},
    empty: boolean,
    apply: (target: EngineCallFields, primary: boolean) => void,
    settles = false,
): void => {
    const engine = engineOf(action);
    if (!engine || !state.callOwner || state.callOwner === engine) {
        apply(state, true);
        if (engine && !empty && !state.callOwner) {
            state.callOwner = engine;
        }
        if (settles && isSettled(state) && (state.callOwner === engine || !engine)) {
            promoteSecondary(state);
        }
        return;
    }
    if (state.secondaryCall?.engine !== engine) {
        if (empty) {
            return;
        }
        state.secondaryCall = idleEngineCall(engine);
    }
    apply(state.secondaryCall, false);
    if (settles && isSettled(state.secondaryCall)) {
        state.secondaryCall = undefined;
    }
};

const initialState: DialerState = {
    isDialerHidden: true,
    autoHideDialer: true,
    isInCalls: false,
    selectedPhoneNumber: undefined,
    selectedMicrophone: null,
    selectedSpeaker: '',
    incomingCall: false,
    autoGainControl: false,
    callFrom: '',
    callTo: undefined,
    callState: CallStateEnum.IDLE,
    phoneNumber: '',
    selectedPrefix: {
        isoCountry: 'US',
        prefix: '+1',
    },
    showKeypad: false,
    sipInstanceId: null,
    micRequired: false,
    isOffline: false,
    navigateTo: '/',
    ivrKeyName: '',
    isRingbackPlaying: false,
    isConnectingSoundPlaying: false,
    isSpam: 'false',
    isRingingPlaying: false
};

const dialerSlice = createSlice({
    name: 'dialer',
    initialState,
    reducers: {
        setSelectedPhoneNumber(state, action: PayloadAction<any>) {
            state.selectedPhoneNumber = action.payload;
        },
        toggleAutoHideDialer: (state,action: PayloadAction<any> ) => {
            state.autoHideDialer = action.payload;
        },
        toggleDialerHidden: (state) => {
            state.isDialerHidden = !state.isDialerHidden;
        },
        setDialerHidden: (state, action) => {
            state.isDialerHidden = action.payload;
        },
        setIsInCallsAction: (state, action) => {
            state.isInCalls = action.payload;
            if(action.payload) {
                state.isDialerHidden = false;
            }
        },
        setSelectedMicrophoneDevice: (state, action) => {
            state.selectedMicrophone = action.payload;
        },
        setSelectedSpeakerDevice: (state, action) => {
            state.selectedSpeaker = action.payload;
        },
        setIncomingCall: (state, action) => {
            writeEngineCall(state, action, !action.payload, target => {
                target.incomingCall = action.payload;
            }, true);
        },
        setAutoGainControl: (state, action) => {
            state.autoGainControl = action.payload;
        },
        setCallFrom: (state, action) => {
            writeEngineCall(state, action, action.payload == null, (target, primary) => {
                target.callFrom = action.payload;
                if (primary) {
                    state.showKeypad = false;
                }
            });
        },
        setCallTo: (state, action) => {
            writeEngineCall(state, action, action.payload == null, target => {
                target.callTo = action.payload;
            });
        },
        setCallState: {
            prepare: (callState: CallStateEnum | `${CallStateEnum}`) => ({payload: callState as CallStateEnum, meta: {at: Date.now()}}),
            reducer: (state, action: PayloadAction<CallStateEnum, string, {at: number; engine?: CallEngineTag}>) => {
                let primaryWrite = false;
                let previousState = state.callState;
                writeEngineCall(state, action, action.payload === CallStateEnum.IDLE, (target, primary) => {
                    primaryWrite = primary;
                    previousState = target.callState;
                    target.callState = action.payload;
                    if (action.payload === CallStateEnum.IN_CALL && previousState !== CallStateEnum.IN_CALL) {
                        target.inCallSince = action.meta.at;
                    } else if (action.payload !== CallStateEnum.IN_CALL) {
                        target.inCallSince = undefined;
                    }
                }, true);
                if (!primaryWrite) {
                    return;
                }

                if (
                    (previousState === CallStateEnum.CONNECTING && action.payload !== CallStateEnum.CONNECTING) ||
                    action.payload === CallStateEnum.IN_CALL ||
                    action.payload === CallStateEnum.IDLE ||
                    (state.isRingbackPlaying && action.payload !== CallStateEnum.CONNECTING)
                ) {
                    state.isRingbackPlaying = false;
                }
                if (action.payload !== CallStateEnum.CONNECTING) {
                    state.isConnectingSoundPlaying = false;
                }
            },
        },
        setPhoneNumber: (state, action) => {
            state.phoneNumber = action.payload;
            state.isDialerHidden = false;

        },
        setSelectedPrefix: (state, action) => {
            state.selectedPrefix =  {
                isoCountry: action.payload.isoCountry,
                prefix:action.payload.prefix,
            };
        },
        setShowKeypad: (state, action) => {
            state.showKeypad = action.payload;
        },
        setSipInstanceId: (state, action: PayloadAction<{ sipInstanceId: string }>) => {
            state.sipInstanceId = action.payload.sipInstanceId;
        },
        setMicRequired: (state, action: PayloadAction<boolean>) => {
            state.micRequired = action.payload;
        },
        setIsOnlineOffline: (state, action: PayloadAction<boolean>) => {
            state.isOffline = action.payload;
        },
        navigateDialer: (state, action) => {
            state.navigateTo = action.payload;
        },
        clearDialerNavigation: (state) => {
            state.navigateTo = null;
        },
        setIvrKeyName: (state, action) => {
            writeEngineCall(state, action, !action.payload, target => {
                target.ivrKeyName = action.payload;
            });
        },
        playRingback: (state) => {
            state.isRingbackPlaying = true;
            state.isConnectingSoundPlaying = false;
        },
        stopRingback: (state) => {
            state.isRingbackPlaying = false;
        },
        playConnectingSound: (state) => {
            state.isConnectingSoundPlaying = true;
            state.isRingbackPlaying = false;
        },
        stopConnectingSound: (state) => {
            state.isConnectingSoundPlaying = false;
        },
        setSpam: (state, action) => {
            writeEngineCall(state, action, !action.payload || action.payload === 'false', target => {
                target.isSpam = action.payload;
            });
        },
        playRingtone: (state) => {
            state.isRingingPlaying = true;
        },
        stopRingtone: (state) => {
            state.isRingingPlaying = false;
        },
    },
});

export const {setSelectedPhoneNumber} = dialerSlice.actions;

export const {
    toggleDialerHidden,
    toggleAutoHideDialer,
    setDialerHidden,
    setIsInCallsAction,
    setSelectedMicrophoneDevice,
    setSelectedSpeakerDevice,
    setIncomingCall,
    setAutoGainControl,
    setCallFrom,
    setCallTo,
    setIvrKeyName,
    setCallState,
    setPhoneNumber,
    setSelectedPrefix,
    setShowKeypad,
    setSipInstanceId,
    setMicRequired,
    setIsOnlineOffline,
    navigateDialer,
    clearDialerNavigation,
    playRingback,
    stopRingback,
    playConnectingSound,
    stopConnectingSound,
    setSpam,
    playRingtone,
    stopRingtone

} = dialerSlice.actions;

export const selectSelectedPhoneNumber = (state: RootState) =>
    state.dialer.selectedPhoneNumber;
export const getSelectedMicrophone = (state: RootState) =>
    state.dialer.selectedMicrophone;
export const getSelectedSpeaker = (state: RootState) =>
    state.dialer.selectedSpeaker;
export const getAutoGainControl = (state: RootState) =>
    state.dialer.autoGainControl;
export const getCallTo = (state: RootState) =>
    state.dialer.callTo;
export const getIsRingbackPlaying = (state: RootState) =>
    state.dialer.isRingbackPlaying;
export const getIsConnectingSoundPlaying = (state: RootState) =>
    state.dialer.isConnectingSoundPlaying;

export default dialerSlice.reducer;
export const getIsDialerHidden = (state: RootState) => state.dialer.isDialerHidden;
export const selectSelectedPrefix = (state: RootState) => state.dialer.selectedPrefix;
export const getShowKeypad = (state: RootState) => state.dialer.showKeypad;
export const getMicRequired = (state: RootState) => state.dialer.micRequired;

export const getIvrKeyName = (state: RootState) => state.dialer.ivrKeyName;
export const getSpam = (state: RootState) => state.dialer.isSpam;

const ENGINE_ACTIONS: ReadonlySet<string> = new Set([
    setCallState.type,
    setIncomingCall.type,
    setCallFrom.type,
    setCallTo.type,
    setSpam.type,
    setIvrKeyName.type,
]);

export const tagEngine = <D extends Dispatch>(engine: CallEngineTag, dispatch: D): D => {
    const tagged = (action: unknown) => {
        if (action && typeof action === 'object' && ENGINE_ACTIONS.has((action as UnknownAction).type)) {
            const current = action as UnknownAction & {meta?: object};
            return dispatch({...current, meta: {...current.meta, engine}} as UnknownAction);
        }
        return dispatch(action as UnknownAction);
    };
    return tagged as D;
};

export const engineCallOf = (dialer: DialerState, engine: CallEngineTag): EngineCallFields | undefined => {
    if (dialer.callOwner === engine) {
        return dialer;
    }
    if (dialer.secondaryCall?.engine === engine) {
        return dialer.secondaryCall;
    }
    return undefined;
};
export const dialerPersistConfig = {
    blacklist: ['callState', 'inCallSince', 'ivrKeyName', 'incomingCall', 'callFrom', 'callTo', 'isRingbackPlaying', 'isConnectingSoundPlaying', 'savedSipInstanceId', 'callOwner', 'secondaryCall']
};